import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap,
  BookOpen,
  Sparkle,
  ChartLineUp,
  ChatCircleDots,
  ShieldCheck,
  ArrowRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";

const features = [
  { icon: BookOpen, title: "Cours & Formations", desc: "Créez, publiez et suivez des cours riches avec leçons, devoirs et quiz." },
  { icon: Sparkle, title: "Assistant IA", desc: "Un assistant pédagogique et un générateur de quiz propulsés par l'IA." },
  { icon: ChartLineUp, title: "Notes & Analytique", desc: "Carnet de notes, tableaux de bord et suivi des performances en temps réel." },
  { icon: ChatCircleDots, title: "Communication", desc: "Messagerie, forums de cours et notifications instantanées." },
  { icon: ShieldCheck, title: "Rôles fins", desc: "Permissions précises pour Administrateurs, Professeurs et Étudiants." },
  { icon: GraduationCap, title: "Gestion académique", desc: "Campus, filières, promotions et semestres organisés simplement." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user) navigate("/app");
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <GraduationCap size={24} weight="fill" />
            </div>
            <span className="font-display text-lg font-extrabold tracking-tight text-primary">ECL Abidjan</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button className="bg-primary hover:bg-primary/90" data-testid="header-login">Connexion</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="grid-pattern">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-accent">
              École de Commerce de Lyon
            </p>
            <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              La plateforme <span className="text-primary">e-learning</span> de nouvelle génération
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Toute la puissance de Moodle, une interface moderne et une intelligence artificielle
              intégrée — pensée pour le Campus d'Abidjan.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login">
                <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="hero-cta">
                  Accéder à mon espace <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">Comptes créés par l'administration de l'école. Contactez l'administrateur pour obtenir vos identifiants.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-2xl border border-border shadow-2xl">
              <img
                src="https://images.pexels.com/photos/10744384/pexels-photo-10744384.jpeg"
                alt="Étudiants Campus Abidjan"
                className="h-full max-h-[70vh] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-xl border border-border bg-card p-4 shadow-lg">
              <p className="font-display text-2xl font-extrabold text-primary">12</p>
              <p className="text-xs text-muted-foreground">modules pédagogiques</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <h2 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
          Une plateforme complète, pas une simple copie
        </h2>
        <p className="mt-2 text-muted-foreground">Conçue pour un établissement d'enseignement supérieur moderne.</p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-xl border border-border bg-card p-6 transition-transform hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary text-primary">
                <f.icon size={26} weight="duotone" />
              </div>
              <h3 className="font-display text-lg font-bold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-6 py-8 text-sm text-muted-foreground">
          © {new Date().getFullYear()} École de Commerce de Lyon – Campus Abidjan.
        </div>
      </footer>
    </div>
  );
}
